import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
    Box,
    Paper,
    Snackbar,
    Alert,
    Stack,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import CustomModalHeader from "../components/custom_components/CustomModalHeader";
import CustomButton from "../components/custom_components/CustomButton";

const apiUrl = process.env.REACT_APP_API_URL;

export default function CopyPaymentSummary() {
    const { token } = useParams();

    const [imageUrl, setImageUrl] = useState("");
    const [copied, setCopied] = useState(false);
    const [error, setError] = useState("");

    // const [text, setText] = useState("");
    const [loading, setLoading] = useState(true);

    // INITIALIZED IMG
    useEffect(() => {
        if (!token) {
            setError("Invalid or missing QR code token.");
            setLoading(false);
            return;
        }

        setLoading(true);
        setError("");

        const url =
            `${apiUrl}shared_text/get_temp_image.php?token=${encodeURIComponent(token)}`;

        const image = new Image();

        image.onload = () => {
            setImageUrl(url);
            setLoading(false);
        };

        image.onerror = () => {
            setError("Unable to load image. It may have expired or been deleted.");
            setLoading(false);
        };

        image.src = url;

        return () => {
            image.onload = null;
            image.onerror = null;
        };
    }, [token]);

    // COPY IMAGE
    const handleCopyImage = async () => {
        try {
            if (
                !navigator.clipboard?.write ||
                !window.ClipboardItem ||
                !window.isSecureContext
            ) {
                throw new Error(
                    "Image clipboard is not supported in this browser. Try HTTPS or a supported desktop browser."
                );
            }

            const response = await fetch(imageUrl);

            if (!response.ok) {
                throw new Error("Unable to fetch image. It may have expired.");
            }

            const blob = await response.blob();

            let pngBlob;

            if (blob.type === "image/png") {
                pngBlob = blob;
            } else {
                const bitmap = await createImageBitmap(blob);

                const canvas = document.createElement("canvas");
                canvas.width = bitmap.width;
                canvas.height = bitmap.height;

                const ctx = canvas.getContext("2d");
                ctx.drawImage(bitmap, 0, 0);
                bitmap.close();

                pngBlob = await new Promise((resolve, reject) => {
                    canvas.toBlob((result) => {
                        if (result) resolve(result);
                        else reject(new Error("PNG conversion failed."));
                    }, "image/png");
                });
            }

            await navigator.clipboard.write([
                new ClipboardItem({ "image/png": pngBlob }),
            ]);

            setCopied(true);
        } catch (error) {
            console.error("Copy image error:", error);
            setError(true);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100dvh",
                bgcolor: "#f5f5f5",
                p: 2,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                boxSizing: "border-box",
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    p: 3,
                    width: "100%",
                    maxWidth: 500,
                    borderRadius: 1.5,
                }}
            >

                <CustomModalHeader
                    title="Payment Summary"
                    subtitle="You can now copy this image and paste it."
                />

                {loading ? (
                    "Loading image..."
                ) : error ? (
                    error
                ) : imageUrl ? (
                    <img
                        src={imageUrl}
                        alt="Temporarily shared image"
                        style={{
                            maxWidth: "100%",
                            maxHeight: "80vh",
                            objectFit: "contain",
                            display: "block",
                            margin: "0 auto",
                        }}
                    />
                ) : (
                    "No image was provided."
                )}

                <Stack sx={{ mt: 2 }}></Stack>

                <CustomButton
                    label="Copy"
                    width="100%"
                    startIcon={<ContentCopyIcon />}
                    disabled={loading}
                    onClick={handleCopyImage}
                />

                <Snackbar
                    open={copied}
                    autoHideDuration={2500}
                    onClose={() => setCopied(false)}
                >
                    <Alert severity="success">
                        Image copied! You can now paste it.
                    </Alert>
                </Snackbar>

                <Snackbar
                    open={error}
                    autoHideDuration={3500}
                    onClose={() => setError(false)}
                >
                    <Alert severity="error">
                        Clipboard access failed. Use HTTPS and try again.
                    </Alert>
                </Snackbar>
            </Paper>
        </Box>
    );
}